import type { UnhandledRequestCallback } from "msw";
import { setupWorker } from "msw/browser";

import { handlers as routerHandlers } from "./handlers";
import { mockHandlerRegistry } from "./registry";

export const worker = setupWorker();

/**
 * With mocking on, the run must be hermetic (adr/0003): let same-machine
 * traffic through (the router/connect/tech-ui dev servers, HMR, static assets,
 * and the proxied `/locales` all live on localhost) but fail loudly on any
 * request to another host, so a missing app-side handler can never silently
 * reach a real backend through a blanket bypass.
 */
const rejectNonLocalRequests: UnhandledRequestCallback = (request) => {
  const { hostname } = new URL(request.url);
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]"
  ) {
    return;
  }
  throw new Error(
    `[e2e] Blocked an unhandled request to a non-local host: ${request.method} ${request.url}`
  );
};

export async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_MOCK_API !== "true") {
    return;
  }

  // Register the router's own handlers (shell + tech-ui endpoints); connect
  // adds its handlers later via Module Federation (see main.tsx).
  mockHandlerRegistry.register(routerHandlers);

  const basePath = (import.meta.env.VITE_BASE_URL as string | undefined) || "/";

  // Start worker first with empty handlers
  await worker.start({
    serviceWorker: {
      url: `${basePath}mockServiceWorker.js`,
    },
    onUnhandledRequest: rejectNonLocalRequests,
  });

  // Dynamically add handlers from registry as micro-frontends register them
  const handlers = mockHandlerRegistry.getHandlers();
  if (handlers.length > 0) {
    worker.use(...handlers);
  }
}
